import React, {useEffect, useRef, useState, useCallback} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import ScrollLock from 'react-scrolllock'
import {KEY_JANUS, useTranslation} from '@modifi/plugin-i18n'

import {
  CountryAwareBrandLogo,
  SignUpButton,
  LoginButton,
  NavigationItem,
  RegionAndLocaleDropdown,
} from './components'
import {
  Header,
  PrimaryNavList,
  MainNav,
  LogoWrapper,
  NavToggle,
  Divider,
  DesktopNavigationWrapper,
  MobileNavigationWrapper,
  RegionLocaleNavItemWrapper,
} from './styles'
import useMobileNavState from './hooks/use-mobile-nav-state'
import detectPassiveEventsSupport from '../../../utils/detectPassiveEventsSupport'
import {useScreenWidthEffect} from '../../../common-components/screen-size-helper'

const propTypes = {
  disableTransparency: PropTypes.bool,
  headerRoutes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      label: PropTypes.string,
      path: PropTypes.string,
      pathSegment: PropTypes.string,
      removeSubMenuDesktop: PropTypes.bool,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string.isRequired,
          label: PropTypes.string,
          path: PropTypes.string,
        })
      ),
    }).isRequired
  ).isRequired,
  loginUrl: PropTypes.string,
}
const defaultProps = {
  disableTransparency: false,
  loginUrl: undefined,
}

export type HeaderNavigationProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const HeaderNavigation: React.FC<HeaderNavigationProps> = ({
  headerRoutes,
  disableTransparency,
  loginUrl,
}) => {
  const [isTransparent, setIsTransparent] = useState(!disableTransparency)
  const [isHamburger, setIsHamburger] = useState(true)
  const primaryNavRef = useRef<HTMLElement>(null)

  const {navigationHeight, navigationRef, toggleNavigation, isNavOpening, isNavOpen, isNavClosing} =
    useMobileNavState(300)

  // using this translatedLabel to update headernav when key changes to translated value
  // so that header updates from hamburger menu since key can be quite large making
  // header nav as hamburger menu but translated value is short so full width spanned navigation
  const {t} = useTranslation(KEY_JANUS)
  const translatedLabel = t(headerRoutes[0].label || '')

  useScreenWidthEffect(() => {
    if (!primaryNavRef.current) return

    const parent = primaryNavRef.current?.parentElement
    if (!parent) return
    const listWidth = Array.from(parent.children).reduce((sum, el) => sum + el.clientWidth, 0)
    const parentWidth = parent.clientWidth ?? 0
    const {paddingRight, paddingLeft} = window.getComputedStyle(parent)

    setIsHamburger(
      listWidth + Number(paddingRight.slice(0, -2)) + Number(paddingLeft.slice(0, -2)) > parentWidth
    )
  }, [translatedLabel])

  const checkWindowScroll = useCallback(() => {
    const scrollTop = window.scrollY
    setIsTransparent(scrollTop > -30 && scrollTop <= 0)
  }, [])

  const handleNavItemClick = useCallback(() => {
    if (isNavOpen) {
      toggleNavigation()
    }
  }, [isNavOpen, toggleNavigation])

  useEffect(() => {
    if (disableTransparency) {
      setIsTransparent(false)
    }
  }, [disableTransparency])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (disableTransparency) return undefined

    checkWindowScroll()
    if (detectPassiveEventsSupport()) {
      window.addEventListener('scroll', checkWindowScroll, {passive: true})
    } else {
      window.addEventListener('scroll', checkWindowScroll)
    }

    return () => {
      window.removeEventListener('scroll', checkWindowScroll)
    }
  }, [checkWindowScroll, headerRoutes, disableTransparency])

  const headerClassNames = []
  if (isNavOpen) headerClassNames.push('is-open')
  if (!isNavOpen) headerClassNames.push('is-not-open')
  if (isTransparent) headerClassNames.push('is-transparent')
  if (!isTransparent) headerClassNames.push('is-not-transparent')
  if (isHamburger) headerClassNames.push('is-hamburger')
  if (!isHamburger) headerClassNames.push('is-not-hamburger')
  if (isNavOpening) headerClassNames.push('is-nav-opening')
  if (isNavClosing) headerClassNames.push('is-nav-closing')

  return (
    <ScrollLock isActive={isNavOpen}>
      <Header className={headerClassNames.join(' ')} mobileNavHeight={navigationHeight}>
        <DesktopNavigationWrapper fluid isLoginHidden={loginUrl?.includes('.cn') || false}>
          <LogoWrapper>
            <CountryAwareBrandLogo isTransparent={isTransparent && !isNavOpen} />
          </LogoWrapper>
          <MainNav className="desktop-navigation" ref={primaryNavRef}>
            <PrimaryNavList>
              {headerRoutes.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <NavigationItem
                  entry={item}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  collapsable
                  hideSubMenu={item?.removeSubMenuDesktop || false}
                />
              ))}
              {!loginUrl?.includes('.cn') && (
                <>
                  <LoginButton />
                  <Divider height="32px" ml={8} mr={8} />
                </>
              )}
              <SignUpButton />
              <RegionLocaleNavItemWrapper>
                <RegionAndLocaleDropdown isHamburger={false} />
              </RegionLocaleNavItemWrapper>
            </PrimaryNavList>
          </MainNav>
        </DesktopNavigationWrapper>

        <MobileNavigationWrapper>
          <LogoWrapper>
            <CountryAwareBrandLogo
              isTransparent={isTransparent && !isNavOpen && !isNavOpening}
              isDisplayedOnMobile
              handleNavItemClick={handleNavItemClick}
            />
            <NavToggle
              data-target="toggle-mobile-navigation"
              isOpen={isNavOpen}
              onClick={toggleNavigation}
              isTransparent={isTransparent && !isNavOpen}
              aria-label="menu"
              isHamburger={isHamburger}
            >
              <span />
              <span />
              <span />
            </NavToggle>
          </LogoWrapper>
          <MainNav className="mobile-navigation" ref={navigationRef}>
            <PrimaryNavList>
              <SignUpButton isHamburger handleNavItemClick={handleNavItemClick} />
              {headerRoutes.map(item => (
                <NavigationItem
                  entry={item}
                  key={item.label}
                  collapsable={false}
                  handleNavItemClick={handleNavItemClick}
                />
              ))}
              <RegionLocaleNavItemWrapper>
                <RegionAndLocaleDropdown isHamburger />
              </RegionLocaleNavItemWrapper>
            </PrimaryNavList>
            {isNavOpen && !loginUrl?.includes('.cn') ? (
              <LoginButton isHamburger navWidth={navigationRef.current?.clientWidth} />
            ) : null}
          </MainNav>
        </MobileNavigationWrapper>
      </Header>
    </ScrollLock>
  )
}

HeaderNavigation.propTypes = propTypes
HeaderNavigation.defaultProps = defaultProps

// container to prevent the initial navigation rendering without navigation routes
// these are necessary to evaluate the proper width of the navigation in order to decide if we render
// the full or the burger menu nav
const HeaderNavigationContainer: React.FC<HeaderNavigationProps> = ({
  headerRoutes,
  ...restProps
}) => {
  if (headerRoutes.length === 0) {
    return null
  }
  return <HeaderNavigation headerRoutes={headerRoutes} {...restProps} />
}

HeaderNavigationContainer.propTypes = propTypes
HeaderNavigationContainer.defaultProps = defaultProps

export default React.memo(HeaderNavigationContainer)
